import { useSyncExternalStore } from "react";

// Release 1.5.0 polish: "Larger text" from the account menu. Kept on this device (it is about the screen and the eyes in
// front of it, and holds nothing about anyone), like the phone layout. On = the <html> element carries `text-large`,
// which raises the root font size to 115 %; every size in the design system is in rem, so the whole page scales and
// layouts reflow rather than clip. Applied only with release 1.5.0 on (main.tsx); off = 1.4.0, unchanged.

export const LARGE_TEXT_KEY = "summitops.largeText";
export const LARGE_TEXT_CLASS = "text-large";
/** The root font size with larger text on (percent of the browser default). */
export const LARGE_TEXT_PERCENT = 115;

const listeners = new Set<() => void>();

function store(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

let memory: boolean | undefined;

export function readLargeText(): boolean {
  if (memory !== undefined) return memory;
  try {
    return store()?.getItem(LARGE_TEXT_KEY) === "on";
  } catch {
    return false;
  }
}

function syncHtmlClass(): void {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle(LARGE_TEXT_CLASS, readLargeText());
}

/** Applies the stored choice to <html> (main.tsx, release 1.5.0 on only). */
export function applyLargeText(): void {
  syncHtmlClass();
}

export function setLargeText(on: boolean): void {
  try {
    if (on) store()?.setItem(LARGE_TEXT_KEY, "on");
    else store()?.removeItem(LARGE_TEXT_KEY);
  } catch {
    /* storage unavailable: the choice lasts until the tab closes */
  }
  memory = on;
  syncHtmlClass();
  listeners.forEach((l) => l());
}

export function toggleLargeText(): void {
  setLargeText(!readLargeText());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useLargeText(): boolean {
  return useSyncExternalStore(subscribe, readLargeText, () => false);
}

/** Test helper. */
export function resetLargeTextForTests(): void {
  memory = undefined;
  try {
    store()?.removeItem(LARGE_TEXT_KEY);
  } catch {
    /* ignore */
  }
  if (typeof document !== "undefined") document.documentElement.classList.remove(LARGE_TEXT_CLASS);
  listeners.forEach((l) => l());
}
